import { useEffect } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import GameCard from "@/components/GameCard";
import HomeContent from "@/components/HomeContent";
import SEO from "@/components/SEO";
import { useApiGet, prefetchMany } from "@/lib/apiCache";
import { CATEGORY_ORDER, CATEGORY_ICON_BG } from "@/lib/games";
import { Sun, Moon, Sparkle, Star, CalendarBlank } from "@phosphor-icons/react";

const CATEGORY_ICON = {
  "Juwai Teer Results":    Sparkle,
  "Shillong Teer Results": CalendarBlank,
  "Morning Teer Results":  Sun,
  "Night Teer Results":    Moon,
  "Other Teer Results":    Star,
};

export default function Home() {
  const { data, loading, refresh } = useApiGet("/results/latest");
  const rows = data || [];

  useEffect(() => {
    const id = setInterval(refresh, 10_000);
    return () => clearInterval(id);
  }, [refresh]);

  // Warm the cache for every game's per-page data so tapping any card / CTA
  // opens without a spinner. Runs once when the homepage list first arrives.
  useEffect(() => {
    if (!rows.length) return;
    const urls = [];
    rows.forEach((r) => {
      urls.push(`/results/${r.game.slug}?limit=10`);
      urls.push(`/results/${r.game.slug}?limit=500`);
    });
    prefetchMany(urls, 30);
  }, [rows.length]); // eslint-disable-line react-hooks/exhaustive-deps

  function updateOne(slug, newResult) {
    // Optimistically patch the shared "/results/latest" cache too.
    refresh();
  }

  const grouped = CATEGORY_ORDER.map((cat) => ({
    category: cat,
    items: rows.filter((r) => r.game.category === cat).sort((a, b) => a.game.number - b.game.number),
  }));

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Juwai Teer",
    url: "https://teerjuwai.com",
  };

  return (
    <div className="min-h-screen flex flex-col bg-neutral-50">
      <SEO
        title="Juwai Teer | Juwai Teer Result | Teer Result Today | Live FR & SR Results"
        description="Juwai Teer live FR and SR Teer results for all 21 games including Juwai, Shillong, Khanapara, Bhutan and Guwahati. Updated daily with previous results and common numbers."
        path="/"
        jsonLd={jsonLd}
      />
      <Header />

      {/* hero */}
      <section className="border-b border-neutral-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 py-6 sm:py-10">
          <h1 className="font-heading font-black tracking-tight text-2xl sm:text-4xl lg:text-5xl text-neutral-900" data-testid="home-h1">
            Live <span className="text-red-600">Teer Results</span>
          </h1>
          <p className="mt-1.5 max-w-2xl text-xs sm:text-base text-neutral-600">
            FR &amp; SR results for Juwai, Shillong, Khanapara, Bhutan, Guwahati and more — updated daily.
          </p>
        </div>
      </section>

      <main className="flex-1 max-w-7xl w-full mx-auto px-3 sm:px-4 py-4 sm:py-8">
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(9)].map((_, i) => (
              <div key={i} className="h-52 rounded-xl bg-white animate-pulse border border-neutral-200" />
            ))}
          </div>
        ) : (
          grouped.map((group) => {
            const Icon = CATEGORY_ICON[group.category] || Star;
            return (
              <section key={group.category} className="mb-6 sm:mb-8" data-testid={`category-${group.category.toLowerCase().replace(/\s/g, "-")}`}>
                <div className="mb-2.5 sm:mb-3 flex items-center gap-3">
                  <div className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-md font-heading font-bold text-[11px] sm:text-sm tracking-wide uppercase ${CATEGORY_ICON_BG[group.category]}`}>
                    <Icon size={14} weight="fill" />
                    {group.category}
                  </div>
                  <div className="h-px flex-1 bg-neutral-200" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {group.items.map(({ game, result }) => (
                    <GameCard key={game.slug} game={game} result={result} />
                  ))}
                </div>
              </section>
            );
          })
        )}
      </main>

      <HomeContent />

      <Footer />
    </div>
  );
}
